import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import confetti from "canvas-confetti";
import { db } from "@/modules/shared/lib/db-client";
import { toast } from "sonner";
import { reportError } from "@/modules/shared/lib/observability";

// Fired whenever a candidate transitions into the "hired" stage, no
// matter which UI surface triggered the move (kanban drag, action bar
// "Avançar fase", smart_move trigger, candidate detail dropdown).
function fireHireConfetti() {
  if (typeof window === "undefined") return;
  const palette = ["#c6f04c", "#4964A2", "#10b981", "#f59e0b", "#ec4899"];
  const fire = (origin: { x: number; y: number }) => {
    confetti({
      particleCount: 80,
      startVelocity: 45,
      spread: 70,
      ticks: 220,
      scalar: 1.05,
      colors: palette,
      origin,
    });
  };
  fire({ x: 0.15, y: 0.85 });
  fire({ x: 0.5, y: 0.95 });
  fire({ x: 0.85, y: 0.85 });
  setTimeout(() => fire({ x: 0.5, y: 0.6 }), 280);
}

export const PIPELINE_STAGES = [
  { key: "ai_screening", label: "Triagem IA", color: "bg-violet-500" },
  { key: "hr_interview", label: "Entrevista RH", color: "bg-violet-500" },
  { key: "technical_test", label: "Teste Técnico", color: "bg-cyan-500" },
  { key: "technical_interview", label: "Entrevista Técnica", color: "bg-amber-500" },
  { key: "final_interview", label: "Entrevista Final", color: "bg-orange-500" },
  { key: "proposal", label: "Proposta", color: "bg-emerald-500" },
  { key: "hired", label: "Contratado", color: "bg-green-600" },
  { key: "disqualified", label: "Desqualificado", color: "bg-rose-500" },
] as const;

export type PipelineStage = typeof PIPELINE_STAGES[number]["key"];

export interface ATSCandidate {
  id: string;
  job_opening_id: string;
  linkedin_url: string;
  candidate_name: string | null;
  candidate_headline: string | null;
  candidate_location: string | null;
  skills: string[];
  overall_score: number | null;
  technical_match_score: number | null;
  stability_score: number | null;
  seniority_score: number | null;
  analysis_verdict: string | null;
  analysis_summary: string | null;
  analysis_details: any;
  stability_flags: string[];
  experience_summary: any[];
  profile_photo_url: string | null;
  pipeline_stage: PipelineStage;
  status: string;
  error_message: string | null;
  created_at: string;
  analyzed_at: string | null;
  disqualified_at: string | null;
  disqualified_reason: string | null;
  pdf_path: string | null;
  stage_entered_at: string | null;
  last_viewed_at: string | null;
}

export interface CandidateActivity {
  id: string;
  candidate_id: string;
  activity_type: string;
  description: string;
  metadata: any;
  created_by: string | null;
  created_at: string;
}

export interface CandidateInterview {
  id: string;
  candidate_id: string;
  interview_type: string;
  title: string;
  scheduled_at: string;
  duration_minutes: number;
  location: string | null;
  interviewer_names: string[];
  notes: string | null;
  status: string;
  feedback_summary: string | null;
  rating: number | null;
  completed_at: string | null;
  created_at: string;
}

export interface CandidateFeedback {
  id: string;
  candidate_id: string;
  pipeline_stage: string;
  feedback_type: string;
  content: string;
  rating: number | null;
  recommendation: string | null;
  author_name: string | null;
  created_at: string;
}

export function useATSCandidates(jobOpeningId: string) {
  return useQuery({
    queryKey: ["ats-candidates", jobOpeningId],
    queryFn: async () => {
      const { data, error } = await db
        .from("job_candidates")
        .select("*")
        .eq("job_opening_id", jobOpeningId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as ATSCandidate[];
    },
  });
}

export function useATSActivities(candidateId: string | null) {
  return useQuery({
    queryKey: ["ats-activities", candidateId],
    enabled: !!candidateId,
    queryFn: async () => {
      const { data, error } = await db
        .from("audit_events")
        .select("*")
        .eq("event_type", "candidate_activity")
        .eq("entity_id", candidateId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []).map(a => ({
        id: a.id,
        candidate_id: a.entity_id || '',
        activity_type: a.action,
        description: typeof a.payload === 'object' && a.payload !== null ? (a.payload as any).description || '' : '',
        metadata: a.payload,
        created_at: a.created_at,
        created_by: a.performed_by,
        organization_id: a.organization_id,
      })) as CandidateActivity[];
    },
  });
}

export function useATSInterviews(candidateId: string | null) {
  return useQuery({
    queryKey: ["ats-interviews", candidateId],
    enabled: !!candidateId,
    queryFn: async () => {
      const { data, error } = await db
        .from("candidate_interviews")
        .select("*")
        .eq("candidate_id", candidateId!)
        .order("scheduled_at", { ascending: true });
      if (error) throw error;
      return data as CandidateInterview[];
    },
  });
}

export function useATSFeedback(candidateId: string | null) {
  return useQuery({
    queryKey: ["ats-feedback", candidateId],
    enabled: !!candidateId,
    queryFn: async () => {
      // Pull both `feedback` (free-form notes) and `scorecard` (criteria-based
      // evaluations) so the recruiter sees them on the same feed after refresh.
      // Previously only `feedback` rows were rendered, which meant scorecards
      // saved via ScorecardDialog disappeared on reload even though the row
      // landed in the table — bug "ScoreCard não fica gravado após
      // atualização da página".
      const { data, error } = await db
        .from("candidate_evaluations")
        .select("*")
        .eq("candidate_id", candidateId!)
        .in("eval_type", ["feedback", "scorecard"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      // Map to CandidateFeedback shape — for scorecards, synthesize a
      // human-readable summary from the criteria/strengths/weaknesses since
      // the raw row has no `content` column populated.
      type ScorecardCriterion = { name?: string; score?: number; notes?: string };
      type EvaluationRow = {
        id: string;
        candidate_id: string;
        organization_id: string;
        pipeline_stage: string | null;
        eval_type: "feedback" | "scorecard" | string;
        content: string | null;
        rating: number | null;
        recommendation: string | null;
        evaluator_id: string | null;
        evaluator_name: string | null;
        criteria: ScorecardCriterion[] | unknown;
        strengths: string | null;
        weaknesses: string | null;
        notes: string | null;
        created_at: string;
        updated_at: string;
      };
      return ((data || []) as EvaluationRow[]).map(r => {
        const isScorecard = r.eval_type === "scorecard";
        const criteriaList: ScorecardCriterion[] = Array.isArray(r.criteria) ? (r.criteria as ScorecardCriterion[]) : [];
        const scorecardSummary = isScorecard
          ? [
              criteriaList.length
                ? "Critérios:\n" + criteriaList.map((c) => `• ${c.name}: ${c.score}/5${c.notes ? ` — ${c.notes}` : ""}`).join("\n")
                : null,
              r.strengths ? `Pontos fortes: ${r.strengths}` : null,
              r.weaknesses ? `Pontos de melhoria: ${r.weaknesses}` : null,
              r.notes ? `Observações: ${r.notes}` : null,
            ]
              .filter(Boolean)
              .join("\n\n")
          : null;
        return {
          id: r.id,
          candidate_id: r.candidate_id,
          pipeline_stage: r.pipeline_stage,
          content: isScorecard ? scorecardSummary : r.content,
          rating: r.rating,
          recommendation: r.recommendation,
          author_id: r.evaluator_id,
          author_name: r.evaluator_name,
          feedback_type: isScorecard ? 'scorecard' : 'general',
          organization_id: r.organization_id,
          created_at: r.created_at,
          updated_at: r.updated_at,
        };
      }) as CandidateFeedback[];
    },
  });
}

/**
 * applyAddTagTriggers — runtime engine for the add_tag trigger
 * (P0 TT parity, "Etiquetar candidato"). Idempotent: tags already on
 * the candidate are not duplicated.
 */
async function applyAddTagTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, tags")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("config")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "add_tag");
  if (!triggers || triggers.length === 0) return;

  const current = new Set<string>(((cand as any).tags as string[]) || []);
  let added = 0;
  for (const t of triggers as any[]) {
    const list: string[] = Array.isArray(t.config?.tags) ? t.config.tags : [];
    for (const tag of list) {
      if (typeof tag === "string" && tag.trim() && !current.has(tag)) {
        current.add(tag);
        added++;
      }
    }
  }
  if (added === 0) return;

  await db
    .from("job_candidates")
    .update({ tags: Array.from(current) })
    .eq("id", candidateId);

  await db.from("audit_events").insert({
    event_type: "candidate_activity",
    action: "tag_added",
    entity_type: "candidate",
    entity_id: candidateId,
    payload: {
      description: `Etiqueta automática: +${added} etiqueta${added > 1 ? "s" : ""}`,
      stage: enteredStage,
      triggered_by: "add_tag",
    },
  } as any);
}

/**
 * applyAddCommentTriggers — runtime engine for the add_comment trigger.
 * Posts an audit_event "candidate_comment" with the configured text,
 * substituting {{candidate_name}} and {{stage_name}} variables.
 */
async function applyAddCommentTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, candidate_name")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("config")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "add_comment");
  if (!triggers || triggers.length === 0) return;

  const stageLabel = PIPELINE_STAGES.find((s) => s.key === enteredStage)?.label || enteredStage;

  for (const t of triggers as any[]) {
    const tpl: string = String(t.config?.comment || "").trim();
    if (!tpl) continue;
    const text = tpl
      .replace(/{{\s*candidate_name\s*}}/g, (cand as any).candidate_name || "Candidato")
      .replace(/{{\s*stage_name\s*}}/g, stageLabel);
    await db.from("audit_events").insert({
      event_type: "candidate_comment",
      action: "created",
      entity_type: "candidate",
      entity_id: candidateId,
      payload: {
        comment: text,
        triggered_by: "add_comment",
        stage: enteredStage,
      },
    } as any);
  }
}

/**
 * applyRemoveTagTriggers — runtime engine for the remove_tag trigger.
 * Idempotent: tags not on the candidate are silently skipped.
 */
async function applyRemoveTagTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, tags")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("config")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "remove_tag");
  if (!triggers || triggers.length === 0) return;

  const current = new Set<string>(((cand as any).tags as string[]) || []);
  let removed = 0;
  for (const t of triggers as any[]) {
    const list: string[] = Array.isArray(t.config?.tags) ? t.config.tags : [];
    for (const tag of list) {
      if (typeof tag === "string" && current.has(tag)) {
        current.delete(tag);
        removed++;
      }
    }
  }
  if (removed === 0) return;

  await db
    .from("job_candidates")
    .update({ tags: Array.from(current) })
    .eq("id", candidateId);

  await db.from("audit_events").insert({
    event_type: "candidate_activity",
    action: "tag_removed",
    entity_type: "candidate",
    entity_id: candidateId,
    payload: {
      description: `Etiqueta(s) removida(s) automaticamente: ${removed}`,
      stage: enteredStage,
      triggered_by: "remove_tag",
    },
  } as any);
}

/**
 * applyMarkInternalTriggers — runtime engine for the mark_internal trigger.
 * Sets is_internal=true on candidate when entering the configured stage.
 */
async function applyMarkInternalTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, is_internal")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand || (cand as any).is_internal) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("id")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "mark_internal")
    .limit(1);
  if (!triggers || triggers.length === 0) return;

  await db
    .from("job_candidates")
    .update({ is_internal: true } as any)
    .eq("id", candidateId);

  await db.from("audit_events").insert({
    event_type: "candidate_activity",
    action: "marked_internal",
    entity_type: "candidate",
    entity_id: candidateId,
    payload: {
      description: "Marcado como interno automaticamente",
      stage: enteredStage,
      triggered_by: "mark_internal",
    },
  } as any);
}

/**
 * applySurveyTriggers — runtime engine for survey-family triggers
 * (send_survey, send_nps, ask_references). All three follow the same
 * pattern: render config.body with vars and queue an audit_event for the
 * downstream worker to dispatch the actual link/email.
 */
async function applySurveyTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, candidate_email, candidate_name")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("kind, config")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .in("kind", ["send_survey", "send_nps", "ask_references", "ask_reference_feedback"]);
  if (!triggers || triggers.length === 0) return;

  for (const t of triggers as any[]) {
    const cfg = t.config || {};
    const subject = String(cfg.subject || "").replace(
      /{{\s*candidate_name\s*}}/g,
      (cand as any).candidate_name || "Candidato"
    );
    const body = String(cfg.body || "")
      .replace(/{{\s*candidate_name\s*}}/g, (cand as any).candidate_name || "Candidato")
      .replace(/{{\s*num_references\s*}}/g, String(cfg.num_questions ?? 3));

    await db.from("audit_events").insert({
      event_type: "candidate_activity",
      action: `${t.kind}_queued`,
      entity_type: "candidate",
      entity_id: candidateId,
      payload: {
        description: `${t.kind} enfileirado: ${subject}`,
        stage: enteredStage,
        triggered_by: t.kind,
        subject,
        body,
        delay_hours: cfg.delay_hours ?? 0,
        num_questions: cfg.num_questions,
        candidate_email: (cand as any).candidate_email,
      },
    } as any);
  }
}

/**
 * applyEmailCampaignTriggers — runtime for add_to_email_campaign trigger.
 * Records audit_event "campaign_subscription_queued"; downstream worker
 * pushes to MailChimp/Klaviyo/etc. via the campaign_id.
 */
async function applyEmailCampaignTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, candidate_email, candidate_name")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("config")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "add_to_email_campaign");
  if (!triggers || triggers.length === 0) return;

  for (const t of triggers as any[]) {
    const cfg = t.config || {};
    await db.from("audit_events").insert({
      event_type: "candidate_activity",
      action: "campaign_subscription_queued",
      entity_type: "candidate",
      entity_id: candidateId,
      payload: {
        description: `Adicionado à campanha: ${cfg.campaign_name || cfg.campaign_id}`,
        stage: enteredStage,
        triggered_by: "add_to_email_campaign",
        campaign_id: cfg.campaign_id,
        campaign_name: cfg.campaign_name,
        candidate_email: (cand as any).candidate_email,
      },
    } as any);
  }
}

/**
 * applyThirdPartyTriggers — runtime for the third_party (integration) trigger.
 * Records audit_event "third_party_webhook_queued"; downstream worker POSTs
 * the payload to the configured webhook URL.
 */
async function applyThirdPartyTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, candidate_email, candidate_name")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("config")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "third_party");
  if (!triggers || triggers.length === 0) return;

  const stageLabel = PIPELINE_STAGES.find((s) => s.key === enteredStage)?.label || enteredStage;

  for (const t of triggers as any[]) {
    const cfg = t.config || {};
    let renderedPayload = cfg.payload_template || "{}";
    try {
      renderedPayload = renderedPayload
        .replace(/{{\s*candidate_name\s*}}/g, (cand as any).candidate_name || "")
        .replace(/{{\s*candidate_email\s*}}/g, (cand as any).candidate_email || "")
        .replace(/{{\s*stage_name\s*}}/g, stageLabel);
    } catch {
      // ignore
    }
    await db.from("audit_events").insert({
      event_type: "candidate_activity",
      action: "third_party_webhook_queued",
      entity_type: "candidate",
      entity_id: candidateId,
      payload: {
        description: `Webhook 3rd party (${cfg.provider || "custom"}) enfileirado`,
        stage: enteredStage,
        triggered_by: "third_party",
        provider: cfg.provider,
        webhook_url: cfg.webhook_url,
        rendered_payload: renderedPayload,
      },
    } as any);
  }
}

/**
 * applyGroupMeetingTriggers — runtime engine for the group_meeting trigger.
 * Records audit_event "group_meeting_invite_queued"; downstream worker
 * generates RSVP link and sends invite. Multiple candidates entering the
 * stage join the same group session up to max_attendees.
 */
async function applyGroupMeetingTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, candidate_email, candidate_name")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("config")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "group_meeting");
  if (!triggers || triggers.length === 0) return;

  for (const t of triggers as any[]) {
    const cfg = t.config || {};
    await db.from("audit_events").insert({
      event_type: "candidate_activity",
      action: "group_meeting_invite_queued",
      entity_type: "candidate",
      entity_id: candidateId,
      payload: {
        description: `Convite reunião de grupo: ${cfg.meeting_title || "Sessão"}`,
        stage: enteredStage,
        triggered_by: "group_meeting",
        meeting_title: cfg.meeting_title,
        duration_minutes: cfg.duration_minutes,
        max_attendees: cfg.max_attendees,
        invite_text: cfg.invite_text,
        candidate_email: (cand as any).candidate_email,
      },
    } as any);
  }
}

/**
 * applySmartScheduleTriggers — runtime engine for the smart_schedule trigger.
 * Records audit_event with meeting type/duration; downstream worker generates
 * the actual scheduling link and sends invite. For MVP, the audit row IS the
 * queue entry — the worker pulls pending items and dispatches.
 */
async function applySmartScheduleTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, candidate_email, candidate_name")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("config")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "smart_schedule");
  if (!triggers || triggers.length === 0) return;

  for (const t of triggers as any[]) {
    const cfg = t.config || {};
    await db.from("audit_events").insert({
      event_type: "candidate_activity",
      action: "schedule_invite_queued",
      entity_type: "candidate",
      entity_id: candidateId,
      payload: {
        description: `Convite de agendamento enfileirado: ${cfg.meeting_type || "entrevista"}`,
        stage: enteredStage,
        triggered_by: "smart_schedule",
        meeting_type: cfg.meeting_type,
        duration_minutes: cfg.duration_minutes,
        invite_text: cfg.invite_text,
        candidate_email: (cand as any).candidate_email,
      },
    } as any);
  }
}

/**
 * applyRestrictAccessTriggers — runtime engine for the restrict_access trigger.
 * Records audit_event marking restriction. Enforcement is RLS-side (separate task);
 * for now this is a signal/intent record visible in the activity feed.
 */
async function applyRestrictAccessTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, is_restricted")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand) return;
  // Idempotent — skip if already flagged.
  if ((cand as any).is_restricted) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("id")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "restrict_access")
    .limit(1);
  if (!triggers || triggers.length === 0) return;

  // Best-effort: column may not exist in older deployments — wrap in
  // try/catch so the runtime survives until migration 063 lands.
  try {
    await db
      .from("job_candidates")
      .update({ is_restricted: true } as any)
      .eq("id", candidateId);
  } catch {
    // ignore — fall through to audit_event
  }

  await db.from("audit_events").insert({
    event_type: "candidate_activity",
    action: "access_restricted",
    entity_type: "candidate",
    entity_id: candidateId,
    payload: {
      description: "Acesso ao candidato restringido automaticamente",
      stage: enteredStage,
      triggered_by: "restrict_access",
    },
  } as any);
}

/**
 * applyTaskListTriggers — runtime engine for the task_list trigger.
 * Creates one audit_event per task (event_type='candidate_task').
 */
async function applyTaskListTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("config")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "task_list");
  if (!triggers || triggers.length === 0) return;

  let createdCount = 0;
  for (const t of triggers as any[]) {
    const tasks: { title: string; due_in_days: number | null }[] = Array.isArray(t.config?.tasks)
      ? t.config.tasks
      : [];
    for (const task of tasks) {
      if (!task?.title) continue;
      const dueDate =
        task.due_in_days != null
          ? new Date(Date.now() + task.due_in_days * 86400000).toISOString().slice(0, 10)
          : null;
      try {
        await db.from("audit_events").insert({
          event_type: "candidate_task",
          action: "created",
          entity_type: "candidate",
          entity_id: candidateId,
          payload: {
            title: task.title,
            due_date: dueDate,
            completed: false,
            completed_at: null,
            triggered_by: "task_list",
            stage: enteredStage,
          },
        } as any);
        createdCount++;
      } catch {
        // ignore individual failures
      }
    }
  }

  if (createdCount > 0) {
    await db.from("audit_events").insert({
      event_type: "candidate_activity",
      action: "tasks_created",
      entity_type: "candidate",
      entity_id: candidateId,
      payload: {
        description: `${createdCount} tarefa${createdCount > 1 ? "s criadas" : " criada"} automaticamente`,
        stage: enteredStage,
        triggered_by: "task_list",
      },
    } as any);
  }
}

/**
 * applySendMessageTriggers — runtime engine for the send_message trigger.
 * Records an audit event so the email delivery worker can pick it up.
 * Actual SMTP delivery happens server-side; the audit row is the queue entry.
 */
async function applySendMessageTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<void> {
  if (enteredStage === "disqualified") return;

  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, candidate_email, candidate_name")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand) return;

  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("config")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "send_message");
  if (!triggers || triggers.length === 0) return;

  for (const t of triggers as any[]) {
    const { template_id, template_name, delay_hours } = t.config || {};
    if (!template_id) continue;
    await db.from("audit_events").insert({
      event_type: "candidate_activity",
      action: "message_scheduled",
      entity_type: "candidate",
      entity_id: candidateId,
      payload: {
        description: `Mensagem automática agendada: ${template_name || template_id}`,
        stage: enteredStage,
        triggered_by: "send_message",
        template_id,
        template_name,
        delay_hours: delay_hours ?? 0,
        candidate_email: (cand as any).candidate_email,
      },
    } as any);
  }
}

/**
 * evaluateSmartMoveTriggers — runtime engine for the smart_move trigger
 * (P0 #3 Movimento inteligente). Called after a candidate transitions
 * into a stage; finds smart_move triggers attached to that stage on the
 * candidate's vaga, evaluates the condition against the candidate
 * record, and returns the target stage to move to (or null).
 *
 * Stops at the first matching trigger to avoid infinite chains. The
 * caller is responsible for actually performing the follow-up move
 * (see useMoveCandidate.onSuccess).
 */
async function evaluateSmartMoveTriggers(
  candidateId: string,
  enteredStage: PipelineStage
): Promise<PipelineStage | null> {
  // Skip evaluation on terminal stages.
  if (enteredStage === "disqualified" || enteredStage === "hired") return null;

  // Find the candidate + their vaga.
  const { data: cand } = await db
    .from("job_candidates")
    .select("id, job_opening_id, overall_score, analysis_verdict, pipeline_stage")
    .eq("id", candidateId)
    .maybeSingle();
  if (!cand || cand.pipeline_stage !== enteredStage) return null;

  // Pull smart_move triggers for this stage on this vaga.
  const { data: triggers } = await db
    .from("job_opening_triggers")
    .select("id, kind, config, position")
    .eq("job_opening_id", (cand as any).job_opening_id)
    .eq("stage_key", enteredStage)
    .eq("kind", "smart_move")
    .order("position", { ascending: true });
  if (!triggers || triggers.length === 0) return null;

  for (const t of triggers as any[]) {
    const c = t.config || {};
    const source = c.source as "ai_score" | "verdict" | undefined;
    const op = c.operator as "gte" | "lte" | "equals" | "not_equals" | undefined;
    const target = c.target_stage as PipelineStage | undefined;
    if (!source || !op || !target || target === enteredStage) continue;

    let lhs: number | string | null = null;
    if (source === "ai_score") lhs = (cand as any).overall_score;
    else if (source === "verdict") lhs = (cand as any).analysis_verdict;
    if (lhs === null || lhs === undefined) continue;

    let rhs: number | string = c.value;
    if (source === "ai_score") rhs = Number(c.value);

    let matches = false;
    if (op === "gte") matches = Number(lhs) >= Number(rhs);
    else if (op === "lte") matches = Number(lhs) <= Number(rhs);
    else if (op === "equals") matches = lhs === rhs;
    else if (op === "not_equals") matches = lhs !== rhs;
    if (matches) return target;
  }
  return null;
}

export function useMoveCandidate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      candidateId,
      stage,
      fromStage,
      reason,
      skipSmartMove,
    }: {
      candidateId: string;
      stage: PipelineStage;
      fromStage?: string;
      reason?: string;
      // Internal flag to prevent infinite smart-move chains. Public
      // callers should never pass this.
      skipSmartMove?: boolean;
    }) => {
      const updates: any = { pipeline_stage: stage };
      if (stage === "disqualified") {
        updates.disqualified_at = new Date().toISOString();
        updates.disqualified_reason = reason || null;
      }

      // Idempotent update: skip the audit + triggers if the candidate is
      // already at the target stage. Prevents duplicate "Proposta → Contratado"
      // log entries when the button is double-clicked or the optimistic
      // cache lags behind the server.
      const { data: updated, error } = await db
        .from("job_candidates")
        .update(updates)
        .eq("id", candidateId)
        .neq("pipeline_stage", stage)
        .select("id");
      if (error) throw error;
      if (!updated || updated.length === 0) return;

      // Audit insert is fire-and-forget — UI doesn't depend on it.
      const stageLabel = PIPELINE_STAGES.find(s => s.key === stage)?.label || stage;
      const fromLabel = fromStage ? (PIPELINE_STAGES.find(s => s.key === fromStage)?.label || fromStage) : null;
      void db.from("audit_events").insert({
        event_type: "candidate_activity",
        action: stage === "disqualified" ? "disqualified" : "stage_change",
        entity_type: "candidate",
        entity_id: candidateId,
        payload: {
          description: stage === "disqualified"
            ? `Candidato desqualificado: ${reason || "Sem motivo"}`
            : fromLabel
              ? `${fromLabel} → ${stageLabel}`
              : `Movido para etapa: ${stageLabel}`,
          stage,
          from_stage: fromStage || null,
          reason,
        },
      } as any).then(() => {
        queryClient.invalidateQueries({ queryKey: ["ats-activities"] });
      }).catch(() => {});

      // Triggers + smart-move evaluation run in background — UI shows the
      // move immediately. When smart_move changes the stage we invalidate
      // queries so the new position renders.
      if (!skipSmartMove) {
        void (async () => {
          try {
            await Promise.all([
              applyAddTagTriggers(candidateId, stage),
              applyAddCommentTriggers(candidateId, stage),
              applyRemoveTagTriggers(candidateId, stage),
              applyMarkInternalTriggers(candidateId, stage),
              applyRestrictAccessTriggers(candidateId, stage),
              applyTaskListTriggers(candidateId, stage),
              applySendMessageTriggers(candidateId, stage),
              applySmartScheduleTriggers(candidateId, stage),
              applyGroupMeetingTriggers(candidateId, stage),
              applySurveyTriggers(candidateId, stage),
              applyEmailCampaignTriggers(candidateId, stage),
              applyThirdPartyTriggers(candidateId, stage),
            ]);

            const next = await evaluateSmartMoveTriggers(candidateId, stage);
            if (next && next !== stage) {
              await db
                .from("job_candidates")
                .update({ pipeline_stage: next })
                .eq("id", candidateId);
              const nextLabel = PIPELINE_STAGES.find(s => s.key === next)?.label || next;
              void db.from("audit_events").insert({
                event_type: "candidate_activity",
                action: "stage_change",
                entity_type: "candidate",
                entity_id: candidateId,
                payload: {
                  description: `Movimento inteligente: → ${nextLabel}`,
                  stage: next,
                  triggered_by: "smart_move",
                },
              } as any);
              queryClient.invalidateQueries({ queryKey: ["ats-candidates"] });
              queryClient.invalidateQueries({ queryKey: ["ats-activities"] });
            }
          } catch (err) {
            reportError(err as any, "move-ats-candidate-triggers");
          }
        })();
      }
    },
    onMutate: async ({ candidateId, stage }) => {
      // Optimistic UI: instantly reflect the new stage in every cached
      // ats-candidates query so the kanban card moves before the server
      // round-trip completes.
      await queryClient.cancelQueries({ queryKey: ["ats-candidates"] });
      const snapshots = queryClient.getQueriesData<any[]>({ queryKey: ["ats-candidates"] });
      const prevStage = snapshots
        .flatMap(([, list]) => (Array.isArray(list) ? list : []))
        .find((c: any) => c?.id === candidateId)?.pipeline_stage;
      snapshots.forEach(([key, list]) => {
        if (!Array.isArray(list)) return;
        queryClient.setQueryData(
          key,
          list.map(c => (c?.id === candidateId ? { ...c, pipeline_stage: stage } : c))
        );
      });
      // Confetti when transitioning into "hired", regardless of caller.
      if (stage === "hired" && prevStage !== "hired") {
        fireHireConfetti();
      }
      return { snapshots };
    },
    onError: (err: any, _vars, ctx) => {
      // Roll back optimistic state on failure.
      ctx?.snapshots?.forEach(([key, data]: [unknown, unknown]) => {
        queryClient.setQueryData(key as any, data);
      });
      reportError(err, "move-ats-candidate");
      toast.error("Erro ao mover candidato: " + err.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["ats-candidates"] });
    },
  });
}

export function useAddFeedback() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: {
      candidate_id: string;
      pipeline_stage: string;
      content: string;
      rating?: number;
      recommendation?: string;
      author_name?: string;
    }) => {
      const { error } = await db.from("candidate_evaluations").insert({
        candidate_id: data.candidate_id,
        eval_type: "feedback",
        pipeline_stage: data.pipeline_stage,
        content: data.content,
        rating: data.rating ?? null,
        recommendation: data.recommendation ?? null,
        evaluator_name: data.author_name ?? null,
      } as any);
      if (error) throw error;

      await db.from("audit_events").insert({
        event_type: "candidate_activity",
        action: "feedback_added",
        entity_type: "candidate",
        entity_id: data.candidate_id,
        payload: {
          description: `${data.author_name || "Alguém"} adicionou ${data.recommendation ? "avaliação" : "nota"} na etapa ${PIPELINE_STAGES.find(s => s.key === data.pipeline_stage)?.label || data.pipeline_stage}`,
          rating: data.rating, recommendation: data.recommendation,
        },
      } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ats-feedback"] });
      queryClient.invalidateQueries({ queryKey: ["ats-activities"] });
      toast.success("Feedback adicionado!");
    },
    onError: (err: any) => {
      reportError(err, "add-ats-feedback");
      toast.error("Erro: " + err.message);
    },
  });
}

export function useScheduleInterview() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: {
      candidate_id: string;
      interview_type: string;
      title: string;
      scheduled_at: string;
      duration_minutes: number;
      location?: string;
      interviewer_names?: string[];
      notes?: string;
    }) => {
      const { error } = await db.from("candidate_interviews").insert(data);
      if (error) throw error;

      await db.from("audit_events").insert({
        event_type: "candidate_activity",
        action: "interview_scheduled",
        entity_type: "candidate",
        entity_id: data.candidate_id,
        payload: {
          description: `Entrevista agendada: ${data.title} para ${new Date(data.scheduled_at).toLocaleDateString("pt-BR")}`,
          interview_type: data.interview_type,
        },
      } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ats-interviews"] });
      queryClient.invalidateQueries({ queryKey: ["ats-activities"] });
      toast.success("Entrevista agendada!");
    },
    onError: (err: any) => {
      reportError(err, "schedule-ats-interview");
      toast.error("Erro: " + err.message);
    },
  });
}

export function useUpdateInterview() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<CandidateInterview> & { id: string }) => {
      const { error } = await db
        .from("candidate_interviews")
        .update(data)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ats-interviews"] });
      toast.success("Entrevista atualizada!");
    },
  });
}

export function useDeleteInterview() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db
        .from("candidate_interviews")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ats-interviews"] });
      queryClient.invalidateQueries({ queryKey: ["ats-activities"] });
      toast.success("Entrevista removida!");
    },
    onError: (err: any) => {
      reportError(err, "delete-ats-interview");
      toast.error("Erro ao remover entrevista: " + err.message);
    },
  });
}
